import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Award, Plus } from 'lucide-react';

// Metrics worth spotlighting, phrased as behaviours top performers do
// differently. Deltas are computed from the existing benchmark
// distributions (p75 vs p50) — no new calculations.
const SPOTLIGHT = [
  { id: 'training_performance', label: 'stronger capability transfer' },
  { id: 'knowledge_application', label: 'better knowledge application' },
  { id: 'learning_culture', label: 'stronger learning culture' },
  { id: 'roi_percent', label: 'higher learning ROI' },
];

/**
 * TopPerformersCard — "What Top Performers Do Differently". Turns
 * benchmarking from observation into guidance: shows how much higher
 * the top quartile sits vs the industry median on the levers that
 * prevent value leakage.
 */
export default function TopPerformersCard({ benchmarks = [], industryName }) {
  const rows = SPOTLIGHT.map((s) => {
    const b = benchmarks.find((x) => x.metric_id === s.id);
    if (!b || !b.percentile_50 || !b.percentile_75) return null;
    const pct = Math.round(((b.percentile_75 - b.percentile_50) / b.percentile_50) * 100);
    return pct > 0 ? { id: s.id, pct, label: s.label } : null;
  }).filter(Boolean);

  if (rows.length === 0) return null;

  return (
    <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="top-performers-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 ring-1 ring-emerald-200">
            <Award className="h-4 w-4 text-emerald-600" />
          </span>
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-700">What Top Performers Do Differently</p>
        </div>
        <p className="text-sm text-slate-600 mt-3">
          Top quartile {industryName || 'industry'} organisations, vs the industry median:
        </p>
        <div className="space-y-1.5 mt-3">
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-2 text-sm" data-testid={`top-performer-row-${r.id}`}>
              <Plus className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
              <span className="font-bold text-emerald-700 tabular-nums w-12">+{r.pct}%</span>
              <span className="text-slate-700">{r.label}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-3.5 italic">
          The best organisations prevent value leakage before it happens — use these as targets in Impact Modelling.
        </p>
      </CardContent>
    </Card>
  );
}
